import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, QuestionMarkCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import apiClient from '../services/apiClient';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const SupportModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('contact');
  const [formData, setFormData] = useState({
    subject: '',
    category: 'General',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const categories = [
    'General Inquiry',
    'License Request',
    'Activation Issue',
    'Cluster License',
    'Technical Support',
    'Billing & Payment',
    'Feature Request',
    'Bug Report'
  ];

  const faqs = [
    {
      question: 'How do I request a new license?',
      answer: 'Navigate to "License Requests" from the sidebar, upload your cluster.request file, and submit. Our team will review and approve your request within 24 hours.'
    },
    {
      question: 'What is an activation key?',
      answer: 'An activation key is a signed license file (.xlic) for single-node deployments. Upload your activation-*.xlic file in the "Activation Keys" section to get it approved and signed by our team.'
    },
    {
      question: 'How long does license approval take?',
      answer: 'Most license requests are reviewed and approved within 24 hours during business days. Urgent requests can be expedited by contacting support directly.'
    },
    {
      question: 'Can I use one license for multiple clusters?',
      answer: 'No, each license is cryptographically bound to specific hardware fingerprints. You need separate licenses for different clusters.'
    },
    {
      question: 'What if my license expires?',
      answer: 'You will receive email notifications 30 days and 7 days before expiration. Submit a renewal request through the License Requests page before expiration to avoid service interruption.'
    },
    {
      question: 'How do I verify my email address?',
      answer: 'Check your email inbox for a verification code sent during registration. Enter the 6-digit code on the verification page. If you did not receive it, click "Resend Code".'
    },
    {
      question: 'Can I change my email address?',
      answer: 'For security reasons, email addresses cannot be changed directly. Please contact support with your request and we will assist you.'
    },
    {
      question: 'What is a cluster license?',
      answer: 'A cluster license is for multi-node deployments. Upload your cluster-request-*.xlic file containing inventory for all nodes, and our team will configure and sign it based on your cluster requirements.'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await apiClient.post('/support/contact', {
        subject: formData.subject,
        category: formData.category,
        message: formData.message,
        userEmail: user?.email,
        userName: `${user?.firstName} ${user?.lastName}`,
        userCompany: user?.company
      });

      toast.success('Support request sent successfully! We will respond within 24 hours.');
      
      // Reset form
      setFormData({
        subject: '',
        category: 'General',
        message: ''
      });

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error sending support request:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send support request';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-3xl">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200" style={{ backgroundColor: '#197560' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <QuestionMarkCircleIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Xloud Support
                  </h3>
                  <p className="text-sm text-green-100 mt-0.5">We're here to help!</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-green-100 focus:outline-none transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <nav className="flex px-6">
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'contact'
                    ? 'border-[#197560] text-[#197560]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <EnvelopeIcon className="w-4 h-4 inline mr-2" />
                Contact Support
              </button>
              <button
                onClick={() => setActiveTab('faq')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'faq'
                    ? 'border-[#197560] text-[#197560]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <QuestionMarkCircleIcon className="w-4 h-4 inline mr-2" />
                FAQs
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="bg-white p-6 max-h-[500px] overflow-y-auto">
            {activeTab === 'contact' && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Contact Info Display */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">Logged in as</p>
                      <p className="text-sm text-blue-700 mt-1">
                        {user?.firstName} {user?.lastName} ({user?.email})
                      </p>
                      {user?.company && (
                        <p className="text-xs text-blue-600 mt-1">{user.company}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#197560] focus:border-transparent transition-all mt-1"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Subject */}
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`mt-1 ${errors.subject ? 'border-red-500' : ''}`}
                    placeholder="Brief description of your issue"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#197560] focus:border-transparent transition-all mt-1 ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Please provide detailed information about your question or issue..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Our support team typically responds within 24 hours during business days.
                  </p>
                </div>

                {/* Contact Methods */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900 mb-3">Other ways to reach us:</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <EnvelopeIcon className="w-4 h-4" />
                      <span>Email: <a href="mailto:support@xloud.tech" className="text-[#197560] hover:underline">support@xloud.tech</a></span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outline"
                    disabled={loading}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#197560] hover:bg-[#155a4a] text-white px-8 shadow-md hover:shadow-lg transition-all"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : 'Send Message'}
                  </Button>
                </div>
              </form>
            )}

            {activeTab === 'faq' && (
              <div className="space-y-4">
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h4>
                  <p className="text-sm text-gray-600 mt-1">Find quick answers to common questions</p>
                </div>
                
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <h5 className="font-medium text-gray-900 mb-2 flex items-start">
                      <QuestionMarkCircleIcon className="w-5 h-5 text-[#197560] mr-2 mt-0.5 flex-shrink-0" />
                      {faq.question}
                    </h5>
                    <p className="text-sm text-gray-600 ml-7">
                      {faq.answer}
                    </p>
                  </div>
                ))}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <p className="text-sm text-blue-900">
                    <strong>Still have questions?</strong> Switch to the "Contact Support" tab to send us a message, and we'll get back to you within 24 hours.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SupportModal;
